<template>
  <div :class="columnClassName">
    <column-header :activeLayout="activeLayout" :columns="columns" :columnIndex="columnIndex" :components="components" :activeComponent="component" v-on:setActiveComponent="setActiveComponent"></column-header>
    <div>
      <div class="row">
        <div class="col-md-12">
          <h3><i class="fa fa-fw fa-wand-magic-sparkles"></i> {{ _('Reports, dashboards and tools') }}</h3>
          <p class="text-muted">{{ _('Reports, dashboards and tools your assistant has set up for this shop. Approve each one before it is used.') }}</p>
        </div>
      </div>
      <div class="row">
        <div class="col-md-4 col-sm-6 filtercol">
          <div class="form-group">
            <div class="input-group">
              <div class="input-group-addon">{{ _('Type') }}</div>
              <select class="form-control" v-model="kind">
                <option value="">{{ _('All types') }}</option>
                <option value="report">{{ _('Report') }}</option>
                <option value="dashboard">{{ _('Dashboard') }}</option>
                <option value="interface">{{ _('Tool') }}</option>
              </select>
            </div>
          </div>
        </div>
        <div class="col-md-4 col-sm-6 filtercol">
          <div class="form-group">
            <div class="input-group">
              <div class="input-group-addon">{{ _('Display') }}</div>
              <select class="form-control" v-model="status">
                <option value="">{{ _('Current') }}</option>
                <option value="draft">{{ _('Needs approval') }}</option>
                <option value="active">{{ _('Active') }}</option>
                <option value="paused">{{ _('Paused') }}</option>
                <option value="archived">{{ _('Archived') }}</option>
              </select>
            </div>
          </div>
        </div>
      </div>
      <p v-if="loading && !specs.length" class="text-muted"><i class="fa fa-fw fa-cog fa-spin"></i> {{ _('Loading...') }}</p>
      <p v-else-if="!specs.length" class="text-muted">{{ emptyText }}</p>
      <div v-else class="list-group max-spec-list">
        <button
          v-for="spec in specs"
          :key="spec.id"
          type="button"
          class="list-group-item max-spec-row"
          :class="{ 'max-spec-row--attention': needsMyApproval(spec) }"
          v-on:click="openSpec(spec)"
        >
          <span class="max-spec-row__head">
            <strong class="max-spec-row__title">{{ spec.title }}</strong>
            <span class="label" :class="statusClass(spec)">{{ statusLabel(spec) }}</span>
          </span>
          <span class="max-spec-row__meta text-muted">
            {{ kindLabel(spec) }}
            <template v-if="isReportKind(spec) && spec.schedule"> · {{ scheduleSummary(spec.schedule) }}</template>
            <template v-else-if="isReportKind(spec)"> · {{ _('Only on request') }}</template>
            <template v-if="spec.last_run_ts"> · {{ _('Last run') }} {{ $filters.timestampToDate(spec.last_run_ts) }}</template>
          </span>
          <span class="max-spec-row__flags">
            <span v-if="spec.risk === 'egress'" class="max-spec-flag"><i class="fa fa-fw fa-envelope" aria-hidden="true"></i> {{ _('Sends email') }}</span>
            <span v-if="spec.risk === 'write'" class="max-spec-flag"><i class="fa fa-fw fa-pen-to-square" aria-hidden="true"></i> {{ _('Changes data') }}</span>
            <span v-if="spec.contains_personal_data" class="max-spec-flag"><i class="fa fa-fw fa-user-shield" aria-hidden="true"></i> {{ _('Personal data') }}</span>
            <span v-if="spec.last_error" class="max-spec-flag text-danger"><i class="fa fa-fw fa-triangle-exclamation" aria-hidden="true"></i> {{ _('The last run failed') }}</span>
          </span>
        </button>
      </div>
      <button v-if="hasMore" type="button" class="btn btn-default" :disabled="loading" v-on:click="loadMore">{{ _('Show more') }}</button>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import components from '~/lib/components'
import maxspecs from '~/lib/maxspecs'

const PAGE_SIZE = 20

export default {
  data () {
    return {
      specs: [],
      kind: '',
      status: '',
      hasMore: false
    }
  },
  mixins: [components, maxspecs],
  computed: {
    emptyText () {
      if (this.status) {
        return this._('Nothing matches these filters.')
      }
      if (this.kind === 'report') {
        return this._('Nothing here yet. Ask your assistant in Johku to set up a report.')
      }
      if (this.kind === 'dashboard') {
        return this._('Nothing here yet. Ask your assistant in Johku to set up a dashboard.')
      }
      if (this.kind === 'interface') {
        return this._('Nothing here yet. Ask your assistant in Johku to set up a tool.')
      }
      return this._('Nothing here yet. Ask your assistant in Johku to set up a report, dashboard or tool.')
    }
  },
  watch: {
    kind () {
      this.reload()
    },
    status () {
      this.reload()
    }
  },
  methods: {
    async load (offset) {
      this.loading = true
      let response
      try {
        let url = this.baseDomain + this.shopId + '/maxspecs.json?limit=' + PAGE_SIZE + '&offset=' + offset + '&apikey=' + this.apiKey
        if (this.status) {
          url += '&status=' + encodeURIComponent(this.status)
        }
        if (this.kind) {
          url += '&kind=' + encodeURIComponent(this.kind)
        }
        response = await axios.get(url)
      } catch (e) {
        this.loading = false
        return this.showError(e)
      }
      this.loading = false
      const rows = Array.isArray(response.data) ? response.data : []
      this.specs = offset ? [...this.specs, ...rows] : rows
      this.hasMore = rows.length === PAGE_SIZE
    },
    reload () {
      return this.load(0)
    },
    loadMore () {
      return this.load(this.specs.length)
    },
    openSpec (spec) {
      this.EventBus.$emit('showDetailsColumn', {
        shopId: this.shopId,
        component: 'MaxSpecDetails',
        title: spec.title,
        width: 8,
        data: {
          specId: spec.id
        }
      })
    }
  },
  created () {
    this.reload()
  },
  mounted () {
    if (!this.events) {
      this.events = {
        maxSpecUpdate: () => this.reload()
      }
    }
    this.enableEventBus()
  }
}
</script>

<style scoped>
.max-spec-row {
  display: grid;
  gap: 4px;
  width: 100%;
  text-align: left;
}
.max-spec-row--attention {
  border-left: 3px solid #f0ad4e;
}
.max-spec-row__head {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 8px;
}
.max-spec-row__title {
  text-wrap: pretty;
}
.max-spec-row__meta {
  font-size: 12px;
}
.max-spec-row__flags {
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
  font-size: 12px;
}
.max-spec-row__flags:empty {
  display: none;
}
</style>
